import { useCallback, useEffect, useRef, useState } from "react";

const VERIFY_INTERVAL_MS = 5000;
const VERIFY_TIMEOUT_MS = 60000;
const VERIFY_MAX_ATTEMPTS = 12;
const TERMINAL_STATUSES = new Set([403, 404, 410]);

export const isVerifiedZeroPayment = (payment) => {
  if (payment?.amount === null || payment?.amount === undefined || payment?.amount === "") return false;
  const amount = Number(payment?.amount);
  return payment?.required === false && Number.isFinite(amount) && amount === 0;
};

const retryAfterMs = (error) => {
  const value = error?.response?.headers?.["retry-after"];
  if (!value) return VERIFY_INTERVAL_MS;

  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(VERIFY_INTERVAL_MS, seconds * 1000);

  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(VERIFY_INTERVAL_MS, date - Date.now()) : VERIFY_INTERVAL_MS;
};

const usePaymentVerification = ({
  active,
  scopeKey,
  verify,
  selectPayment,
  onResponse,
  onSettled,
  onProgress,
  initialStartedAt = 0,
  initialAttempts = 0,
  startDelayMs = 0,
}) => {
  const [status, setStatus] = useState(active ? "pending" : "idle");
  const [attempts, setAttempts] = useState(Number(initialAttempts) || 0);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const stoppedRef = useRef(!active);
  const generationRef = useRef(0);
  const requestSequenceRef = useRef(0);
  const appliedRequestRef = useRef(0);
  const settledRef = useRef(false);
  const mountedRef = useRef(false);
  const scopeRef = useRef(scopeKey);
  const startedAtRef = useRef(Number(initialStartedAt) || 0);
  const attemptsRef = useRef(Number(initialAttempts) || 0);
  const initialBudgetRef = useRef({ initialStartedAt, initialAttempts });
  const callbacksRef = useRef({ verify, selectPayment, onResponse, onSettled, onProgress });

  initialBudgetRef.current = { initialStartedAt, initialAttempts };
  callbacksRef.current = { verify, selectPayment, onResponse, onSettled, onProgress };

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stop = useCallback((nextStatus = "idle") => {
    generationRef.current += 1;
    stoppedRef.current = true;
    inFlightRef.current = false;
    settledRef.current = nextStatus === "settled";
    clearTimer();
    setStatus(nextStatus);
  }, [clearTimer]);

  // All payment reads share an ordering, including reads made outside the poller.
  const beginRequest = useCallback(() => ++requestSequenceRef.current, []);
  const isCurrentRequest = useCallback((requestId) =>
    mountedRef.current && requestId >= appliedRequestRef.current, []);
  const acceptResponse = useCallback((data, requestId) => {
    if (!isCurrentRequest(requestId)) return false;
    appliedRequestRef.current = requestId;
    const payment = callbacksRef.current.selectPayment(data);
    const confirmed = isVerifiedZeroPayment(payment);
    if (confirmed) stop("settled");
    else if (settledRef.current) stop();
    callbacksRef.current.onResponse?.(data, payment);
    if (confirmed) callbacksRef.current.onSettled?.(data, payment);
    return true;
  }, [isCurrentRequest, stop]);

  const runRef = useRef(null);

  const schedule = useCallback((delay) => {
    clearTimer();
    if (stoppedRef.current) return;
    const generation = generationRef.current;
    timerRef.current = window.setTimeout(() => {
      if (generation !== generationRef.current || stoppedRef.current) return;
      timerRef.current = null;
      runRef.current?.();
    }, delay);
  }, [clearTimer]);

  const scheduleWithinRun = useCallback((delay) => {
    const elapsed = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
    const remaining = VERIFY_TIMEOUT_MS - elapsed;
    schedule(Math.max(0, Math.min(delay, remaining)));
  }, [schedule]);

  const run = useCallback(async () => {
    if (stoppedRef.current || inFlightRef.current) return;
    if (document.visibilityState === "hidden") {
      setStatus("pending");
      return;
    }

    const now = Date.now();
    if (!startedAtRef.current) startedAtRef.current = now;
    if (
      attemptsRef.current >= VERIFY_MAX_ATTEMPTS ||
      now - startedAtRef.current >= VERIFY_TIMEOUT_MS
    ) {
      stop("timed_out");
      return;
    }

    const generation = generationRef.current;
    const requestId = beginRequest();
    const isCurrentRun = () => generation === generationRef.current && !stoppedRef.current;
    inFlightRef.current = requestId;
    attemptsRef.current += 1;
    setAttempts(attemptsRef.current);
    setStatus("verifying");
    callbacksRef.current.onProgress?.({
      startedAt: startedAtRef.current,
      attempts: attemptsRef.current,
    });

    try {
      const data = await callbacksRef.current.verify();
      if (!isCurrentRun()) return;
      acceptResponse(data, requestId);
      if (!isCurrentRun()) return;
      setStatus("pending");
      scheduleWithinRun(VERIFY_INTERVAL_MS);
    } catch (error) {
      if (!isCurrentRun()) return;
      if (requestId < appliedRequestRef.current) {
        scheduleWithinRun(VERIFY_INTERVAL_MS);
        return;
      }
      const responseStatus = error?.response?.status;
      if (TERMINAL_STATUSES.has(responseStatus)) {
        stop("error");
        return;
      }

      setStatus("unavailable");
      scheduleWithinRun(responseStatus === 429 ? retryAfterMs(error) : VERIFY_INTERVAL_MS);
    } finally {
      if (inFlightRef.current === requestId) inFlightRef.current = false;
    }
  }, [acceptResponse, beginRequest, scheduleWithinRun, stop]);

  runRef.current = run;

  const restart = useCallback(() => {
    if (inFlightRef.current) return;
    generationRef.current += 1;
    clearTimer();
    settledRef.current = false;
    stoppedRef.current = false;
    startedAtRef.current = Date.now();
    attemptsRef.current = 0;
    setAttempts(0);
    setStatus("pending");
    callbacksRef.current.onProgress?.({ startedAt: startedAtRef.current, attempts: 0 });
    runRef.current?.();
  }, [clearTimer]);

  useEffect(() => {
    mountedRef.current = true;
    generationRef.current += 1;
    clearTimer();
    if (scopeRef.current !== scopeKey) {
      scopeRef.current = scopeKey;
      settledRef.current = false;
      appliedRequestRef.current = ++requestSequenceRef.current;
    }
    if (active) settledRef.current = false;
    inFlightRef.current = false;
    stoppedRef.current = !active || settledRef.current;
    startedAtRef.current = Number(initialBudgetRef.current.initialStartedAt) || 0;
    attemptsRef.current = Number(initialBudgetRef.current.initialAttempts) || 0;
    setAttempts(attemptsRef.current);
    setStatus(settledRef.current ? "settled" : active ? "pending" : "idle");
    if (!stoppedRef.current) {
      if (startDelayMs > 0) schedule(startDelayMs);
      else runRef.current?.();
    }

    const resumeIfVisible = () => {
      if (!stoppedRef.current && document.visibilityState !== "hidden" && !inFlightRef.current) {
        clearTimer();
        runRef.current?.();
      }
    };
    document.addEventListener("visibilitychange", resumeIfVisible);
    window.addEventListener("focus", resumeIfVisible);
    window.addEventListener("pageshow", resumeIfVisible);

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      stoppedRef.current = true;
      clearTimer();
      document.removeEventListener("visibilitychange", resumeIfVisible);
      window.removeEventListener("focus", resumeIfVisible);
      window.removeEventListener("pageshow", resumeIfVisible);
    };
  }, [active, clearTimer, schedule, scopeKey, startDelayMs]);

  return { status, attempts, restart, stop, beginRequest, acceptResponse, isCurrentRequest };
};

export default usePaymentVerification;
